import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight, Instagram } from "lucide-react";

const footerLinks = {
  company: [
    { name: "About", hash: "about" },
    { name: "Services", hash: "services" },
    { name: "Why Us", hash: "why-us" },
    { name: "Contact", hash: "contact" },
  ],
  services: [
    { name: "Product Sourcing", hash: "services" },
    { name: "Trading & Distribution", hash: "services" },
    { name: "E-Commerce", hash: "services" },
    { name: "Partnership Programs", hash: "services" },
  ],
};

// WhatsApp icon component
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

export function Footer() {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const hrefFor = (hash: string) => (isHome ? `#${hash}` : `/#${hash}`);
  const handleHashClick = (e: React.MouseEvent, hash: string) => {
    if (!isHome) return;
    e.preventDefault();
    const target = document.getElementById(hash);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const whatsappNumber = "919319266996";
  const whatsappMessage = encodeURIComponent("Hello! I'm interested in learning more about SAILKHA TRADE's products and services.");

  return (
    <footer className="bg-foreground text-background relative">
      <div className="container mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <span className="font-logo text-2xl font-semibold tracking-wide text-background mb-8 block">
              SAILKHA TRADE
            </span>
            <p className="text-background/75 max-w-md mb-8 leading-relaxed">
              SAILKHA TRADE is India's trusted trading and e-commerce company. We connect 
              businesses with quality industrial and construction products through expert B2B sourcing, 
              wholesale trading, and end-to-end e-commerce solutions.
            </p>
            
            {/* Contact */}
            <div className="space-y-3 mb-8">
              <a 
                href="mailto:info.sailkha@gmail.com" 
                className="flex items-center gap-3 text-background/75 hover:text-background transition-colors text-sm"
              >
                <Mail className="w-4 h-4" />
                info.sailkha@gmail.com
              </a>
              <a 
                href="tel:+919319266996" 
                className="flex items-center gap-3 text-background/75 hover:text-background transition-colors text-sm"
              >
                <Phone className="w-4 h-4" />
                +91 93192 66996
              </a>
              <span className="flex items-center gap-3 text-background/75 text-sm">
                <MapPin className="w-4 h-4" />
                India
              </span>
            </div>
            
            {/* Social & WhatsApp */}
            <div className="flex items-center gap-4">
              <a
                href="https://www.instagram.com/sailkhatrade/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-11 h-11 border border-background/30 text-background/75 hover:text-background hover:border-background/40 transition-colors"
                aria-label="Follow SAILKHA TRADE on Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <motion.a
                href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                className="inline-flex items-center gap-3 px-5 py-3 bg-[#25D366] text-foreground text-sm font-medium hover:bg-[#20BD5A] transition-colors"
              >
                <WhatsAppIcon className="w-4 h-4" />
                Chat on WhatsApp
              </motion.a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-xs tracking-wide-elegant uppercase text-background/65 mb-6">
              Company
            </h4>
            <ul className="space-y-4">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={hrefFor(link.hash)}
                    onClick={(e) => handleHashClick(e, link.hash)}
                    className="text-background/75 hover:text-background transition-colors flex items-center gap-2 group text-sm"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-xs tracking-wide-elegant uppercase text-background/65 mb-6">
              Services
            </h4>
            <ul className="space-y-4">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={hrefFor(link.hash)}
                    onClick={(e) => handleHashClick(e, link.hash)}
                    className="text-background/75 hover:text-background transition-colors flex items-center gap-2 group text-sm"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-background/10 mt-16 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-background/65 text-sm space-y-1">
            <p>© {currentYear} SAILKHA TRADE. All rights reserved.</p>
            <p className="text-background/65 text-xs">
              SAILKHA TRADE is a brand of Sailkha Private Limited.
            </p>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="text-background/65 hover:text-background transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="text-background/65 hover:text-background transition-colors">
              Terms
            </Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("sailkha:open-cookie-consent"))}
              className="text-background/65 hover:text-background transition-colors"
            >
              Cookie settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
